import { useState, useMemo, useEffect } from 'react';
import { toast, ToastContainer } from "react-toastify";
import { FaChevronRight } from 'react-icons/fa';
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { useGetInquiries } from '../../queries/helpDesk';
import { useNavigate } from 'react-router-dom';
import Chip from '../../components/Chip';

const Inquiries = () => {
    const navigate = useNavigate();
    // Pagination state
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        username: '',
        subject: '',
        status: 'pending',
    });
    const { data: inquiries, isLoading, isError } = useGetInquiries();
    const [filteredData, setFilteredData] = useState(inquiries?.data || []);

    // Update filteredData when inquiries change
    useEffect(() => {
        setFilteredData(inquiries?.data || []);
    }, [inquiries]);

    // Filtering logic
    useEffect(() => {
        let filtered = inquiries?.data || [];
        if (search) {
            filtered = filtered.filter(inquiry =>
                inquiry.createdBy.username.toLowerCase().includes(search.toLowerCase()) ||
                inquiry.subject.toLowerCase().includes(search.toLowerCase()) ||
                inquiry.firstName.toLowerCase().includes(search.toLowerCase()) ||
                inquiry.lastName.toLowerCase().includes(search.toLowerCase()) ||
                inquiry.email.toLowerCase().includes(search.toLowerCase()) ||
                inquiry.description.toLowerCase().includes(search.toLowerCase())
            );
        }
        if (filters.username) {
            filtered = filtered.filter(inquiry =>
                inquiry.createdBy.username.toLowerCase().includes(filters.username.toLowerCase())
            );
        }
        if (filters.subject) {
            filtered = filtered.filter(inquiry =>
                inquiry.subject.toLowerCase().includes(filters.subject.toLowerCase())
            );
        }
        if (filters.status) {
            filtered = filtered.filter(inquiry =>
                inquiry.status === filters.status
            );
        }
        setFilteredData(filtered);
        setPage(1); // Reset to first page on filter
    }, [search, filters, inquiries]);

    // Status toggle handler
    const handleStatusToggle = (id) => {
        setFilteredData(prev =>
            prev.map(inquiry =>
                inquiry._id === id ? {
                    ...inquiry,
                    status: inquiry.status === 'pending' ? 'resolved' : 'pending'
                } : inquiry
            )
        );
        toast.success('Status updated!');
    };

    // Format date helper
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Table columns
    const columnHelper = createColumnHelper();
    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr. No.',
            cell: info => (page - 1) * pageSize + info.row.index + 1,
        }),
        columnHelper.accessor('createdBy.username', {
            header: 'User Name',
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('subject', {
            header: 'Subject',
            cell: info => (
                <div className="max-w-xs truncate" title={info.getValue()}>{info.getValue()}</div>
            ),
        }),
        columnHelper.display({
            id: 'status',
            header: 'Status',
            align: 'center',
            cell: info => (
                <Chip
                    value={info.row.original.status}
                    color={info.row.original.status === 'pending' ? 'inactive' : 'green'}
                />
            ),
        }),
        columnHelper.accessor('createdAt', {
            header: 'Created',
            align: 'center',
            cell: info => (
                <div className="text-sm text-gray-600">
                    {formatDate(info.getValue())}
                </div>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            align: 'center',
            cell: info => (
                <button
                    className="bg-[#F4F8FB] rounded p-2 hover:bg-[#e0e7ef]"
                    title="View Details"
                    onClick={() => {
                        // Navigate to inquiry details page - adjust path as needed
                        navigate(`/help-desk/inquiry-details/${info.row.original._id}`);
                    }}
                >
                    <FaChevronRight className="text-[#B5B5C3]" />
                </button>
            ),
        }),
    ], [page, navigate]);

    // Pagination logic
    const paginatedData = filteredData.slice((page - 1) * pageSize, page * pageSize);
    const totalPages = Math.ceil(filteredData.length / pageSize);

    if (isLoading) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <h1 className="text-2xl font-bold text-[#2ECC40] mb-2">Help Desk - Inquiries</h1>
                <div className="bg-white rounded-md shadow p-4 mb-4 text-center">
                    <div className="text-gray-500">Loading inquiries...</div>
                </div>
            </main>
        );
    }

    if (isError) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <h1 className="text-2xl font-bold text-[#2ECC40] mb-2">Help Desk - Inquiries</h1>
                <div className="bg-white rounded-md shadow p-4 mb-4 text-center">
                    <div className="text-red-500">Error loading inquiries. Please try again.</div>
                </div>
            </main>
        );
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
            {/* Header */}
            <h1 className="text-2xl font-bold text-[#2ECC40] mb-2">Help Desk - Inquiries</h1>

            {/* Search Bar */}
            {/* <div className="bg-white rounded-md shadow p-4 mb-4">
        <input
          type="text"
          placeholder="Search by username, subject, name, email, or description..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full border border-gray-200 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-200"
        />
      </div> */}

            {/* Filters */}
            {/* <form className="bg-white rounded-md shadow p-4 mb-4" onSubmit={e => { e.preventDefault(); }}>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm mb-1">User Name</label>
            <input
              type="text"
              name="username"
              value={filters.username}
              onChange={e => setFilters(f => ({ ...f, username: e.target.value }))}
              placeholder="Enter User Name"
              className="w-full border border-gray-200 rounded-md px-3 py-2"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm mb-1">Subject</label>
            <input
              type="text"
              name="subject"
              value={filters.subject}
              onChange={e => setFilters(f => ({ ...f, subject: e.target.value }))}
              placeholder="Enter Subject"
              className="w-full border border-gray-200 rounded-md px-3 py-2"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="w-full border border-gray-200 rounded-md px-3 py-2 bg-white"
            >
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md border border-gray-200"
              onClick={() => {
                setFilters({ username: '', subject: '', status: 'pending' });
                setSearch('');
              }}
            >
              Reset
            </button>
            <button
              type="submit"
              className="bg-[#2ECC40] text-white px-6 py-2 rounded-md hover:bg-green-600"
            >
              Search
            </button>
          </div>
        </div>
      </form> */}

            {/* Table Section */}
            <section className="bg-white rounded-md shadow p-4 mb-4">
                <div className="mb-2 font-semibold">Inquiries List</div>
                <TanStackTable
                    data={inquiries}
                    columns={columns}
                //   enablePagination={false}
                />
                {/* Pagination and count */}
                {/* <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">
            {filteredData.length > 0 ? (page - 1) * pageSize + 1 : 0} out of {filteredData.length}
          </div>
          {totalPages > 1 && (
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`px-3 py-1 rounded ${page === i + 1 ? 'bg-[#2ECC40] text-white' : 'bg-gray-100 text-gray-700'}`}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div> */}
            </section>
            <ToastContainer position="top-right" autoClose={2000} />
        </main>
    );
};

export default Inquiries;